// Fork modalının onay kutulu katalog ağacı (kural 11): kök → alt kategori → yapraklar; kategori
// kutusu üçlü durum (indeterminate), satır sayaçları "seçili / toplam", arama (eşleşen + ataları).
// Sektör paketleri DR1: işaretli paketlerden gelen kalemler "paket" rozetli (kaldırılabilir).
// DR16/DR17: satır `<label>` + 44 px ok, `role=tree` klavye sözleşmesi ve > 80 satırda sanal pencere
// (CheckTree — odak/seçim kimlik bazlı). `isDisabled` (Paketler sayfası): pasif satırın kutusu
// kapalı + neden `title`; alt ağaç toplu işaretlemesi pasif yaprağın durumunu değiştirmez.

import { useMemo, type ReactNode } from "react";
import type { KpiMetric } from "@mahir/shared";
import {
  buildIndicatorTree,
  subtreeLeafIds,
  toggleSubtree,
  triStateFromSelection,
  visibleIdsForSearch,
  type IndicatorTree,
} from "../../lib/kpi-set-editor";
import { useIdsCollapsed } from "../../hooks/use-ids-collapsed";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { categoryBoxState, toggleCategoryEligible } from "../../lib/kpi-package-tree-select";
import { CheckTree, type CheckTreeRow } from "./CheckTree";
import { codeStyle, pillStyle } from "./kpi-form-styles";

export interface CatalogCheckboxTreeProps {
  metrics: KpiMetric[];
  selected: ReadonlySet<string>;
  search: string;
  onChange: (next: Set<string>) => void;
  /** işaretli paketlerin kalemleri — satırda "paket" rozeti */
  packageMembers?: ReadonlySet<string>;
  /** pasif satır nedeni (null → etkin); verilmezse tüm satırlar etkin */
  isDisabled?: (metric: KpiMetric, selected: boolean) => string | null;
  /** sanallaştırılmış kabın en büyük yüksekliği (varsayılan 320) */
  maxHeight?: number;
  /** ağaç her zaman kendi kaydırıcısı (çağıran dış `overflow` kurmaz — tek kaydırıcı) */
  alwaysScroll?: boolean;
  ariaLabel?: string;
  /**
   * Eklenebilir yaprak ölçütü (Paketler seçicisi): verilirse kategori kutusu alt ağacın eklenebilir
   * yapraklarını seçer, etiket tek başına kırpılır, kod/rozet/sayaç sağ sütunda durur. Verilmezse
   * (Set kur) bugünkü davranış ve biçim aynen.
   */
  eligible?: (metric: KpiMetric) => boolean;
  /** `eligible` ile: zaten pakette olan yapraklar (sayaçta "N pakette") */
  inPackage?: ReadonlySet<string>;
  narrow?: boolean;
  /**
   * Aç/kapa kalıcılık anahtarı (ekran başına AYRI — bkz. `CATALOG_TREE_KEYS`); null → kalıcı değil,
   * hep açık başlar (AI önerileri gizli kalmasın).
   */
  collapseKey: string | null;
  /** `eligible` yokken satır etiketinin yanına ek içerik (AI sekmesi: "önerildi" + gerekçe) */
  rowContent?: (metric: KpiMetric) => ReactNode;
}

/** Her tüketici kendi anahtarını kullanır: bir ekranda kapatılan bant başkasında gizlenmez. */
export const CATALOG_TREE_KEYS = {
  forkSet: "paketler-set-kur",
  packageAdd: "paketler-ekle-katalog",
  /** AI sekmesi kalıcı değil: öneriler hiçbir zaman kapalı bantta saklanmaz */
  suggest: null,
} as const;

const EMPTY_SET: ReadonlySet<string> = new Set();

export function CatalogCheckboxTree(props: CatalogCheckboxTreeProps): JSX.Element {
  const { metrics, selected, search, onChange, packageMembers, isDisabled, eligible } = props;
  const inPackage = props.inPackage ?? EMPTY_SET;
  const tree: IndicatorTree<KpiMetric> = useMemo(() => buildIndicatorTree(metrics), [metrics]);
  const visible = useMemo(() => visibleIdsForSearch(tree, search), [tree, search]);
  // aç/kapa kalıcı (kullanıcı + ekran başına); Paketler üye ağacıyla aynı kalıp
  const { collapsed, set: setBand } = useIdsCollapsed(props.collapseKey);

  const rows = useMemo<CheckTreeRow[]>(() => {
    // Arama varken bantlar açık kalır (eşleşen alt ağaç görünsün).
    const isCollapsed = (id: string): boolean => visible === null && collapsed.has(id);
    const out: CheckTreeRow[] = [];
    const hiddenUnder = new Set<string>();
    for (const r of tree.rows) {
      const id = r.node.id;
      if (visible && !visible.has(id)) continue;
      if (r.node.parentId && hiddenUnder.has(r.node.parentId)) {
        hiddenUnder.add(id);
        continue;
      }
      const open = !isCollapsed(id);
      if (r.hasChildren && !open) hiddenUnder.add(id);
      const leaves = r.hasChildren ? subtreeLeafIds(tree, id) : [];
      const picked = leaves.filter((l) => selected.has(l)).length;
      if (eligible && r.hasChildren) {
        const box = categoryBoxState(tree, id, selected, inPackage, eligible, visible);
        out.push({
          id,
          parentId: r.node.parentId,
          depth: r.depth,
          hasChildren: true,
          expanded: open,
          check: box.state,
          checkable: box.eligible > 0,
          disabledReason: box.eligible > 0 ? null : KPI_PACKAGES_COPY.page.categoryNoEligible,
          label: r.node.label,
          weight: r.depth === 0 ? 700 : 600,
          background: r.depth === 0 ? "var(--color-bg)" : undefined,
          meta: (
            <>
              <span style={codeStyle}>{r.node.code}</span>
              <span className="text-xs" style={{ color: "var(--color-text-2)" }}>
                {KPI_PACKAGES_COPY.page.categoryCounter(box.picked, box.eligible, box.inPackage)}
              </span>
            </>
          ),
        });
        continue;
      }
      const reason = isDisabled?.(r.node, selected.has(id)) ?? null;
      out.push({
        id,
        parentId: r.node.parentId,
        depth: r.depth,
        hasChildren: r.hasChildren,
        expanded: open,
        check: triStateFromSelection(id, tree, selected),
        checkable: reason === null,
        disabledReason: reason,
        label: r.node.label,
        weight: r.depth === 0 ? 700 : r.hasChildren ? 600 : 400,
        background: r.depth === 0 ? "var(--color-bg)" : undefined,
        ...(eligible
          ? {
              meta: (
                <>
                  <span style={codeStyle}>{r.node.code}</span>
                  {packageMembers?.has(id) ? (
                    <span style={pillStyle}>{KPI_PACKAGES_COPY.pills.package}</span>
                  ) : null}
                  <span className="text-xs" style={{ color: "var(--color-text-2)" }}>
                    {r.node.defaultUnit ?? ""}
                  </span>
                </>
              ),
            }
          : {
              content: (
                <>
                  <span style={codeStyle}>{r.node.code}</span>
                  {packageMembers?.has(id) ? (
                    <span style={{ ...pillStyle, marginLeft: 6 }}>
                      {KPI_PACKAGES_COPY.pills.package}
                    </span>
                  ) : null}
                  {props.rowContent?.(r.node)}
                </>
              ),
              trailing: (
                <span
                  className="text-xs"
                  style={{ color: "var(--color-text-2)", whiteSpace: "nowrap" }}
                >
                  {r.hasChildren ? `${picked} / ${leaves.length}` : (r.node.defaultUnit ?? "")}
                </span>
              ),
            }),
      });
    }
    return out;
  }, [tree, visible, collapsed, selected, packageMembers, isDisabled, eligible, inPackage]);

  if (rows.length === 0) {
    return (
      <div className="px-3 py-4 text-sm" style={{ color: "var(--color-text-3)" }}>
        Eşleşen gösterge yok
      </div>
    );
  }

  function toggle(id: string): void {
    if (eligible && (tree.childrenOf.get(id)?.length ?? 0) > 0) {
      onChange(toggleCategoryEligible(tree, id, selected, eligible, visible));
      return;
    }
    const next = toggleSubtree(id, tree, selected);
    if (isDisabled) {
      // alt ağaç toplu işaretlemesi pasif yaprağın durumunu değiştirmez
      for (const leaf of subtreeLeafIds(tree, id)) {
        const node = tree.byId.get(leaf)?.node;
        if (!node || isDisabled(node, selected.has(leaf)) === null) continue;
        if (selected.has(leaf)) next.add(leaf);
        else next.delete(leaf);
      }
    }
    onChange(next);
  }

  return (
    <CheckTree
      rows={rows}
      ariaLabel={props.ariaLabel ?? "Katalog göstergeleri"}
      narrow={props.narrow}
      maxHeight={props.maxHeight}
      alwaysScroll={props.alwaysScroll}
      onCheck={toggle}
      onToggleExpand={(id, open) => setBand(id, !open)}
    />
  );
}
